import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'

export interface KeepContactDoneResult {
  /** The reminder that was done (null when it was already done). */
  previousDate: string | null
  /** The next reminder of a repeating follow-up, null otherwise. */
  nextDate: string | null
}

const markDone = async (id: string): Promise<KeepContactDoneResult> => {
  const { data } = await api.post<KeepContactDoneResult>(`/keep-contact/${id}/done`)
  return data
}

const postpone = async ({ id, date }: { id: string; date: string }): Promise<{ date: string }> => {
  const { data } = await api.post<{ date: string }>(`/keep-contact/${id}/postpone`, { date })
  return data
}

const useRefreshFollowUps = () => {
  const queryClient = useQueryClient()
  return async (id: string) => {
    await Promise.all([
      // Every tab and the menu badge.
      queryClient.invalidateQueries({ queryKey: ['notifications'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['keep-contacts'] }),
      queryClient.invalidateQueries({ queryKey: ['keep-contact', id] }),
    ])
  }
}

/**
 * « C'est fait » on a follow-up: a repeating one moves to its next date, a
 * one-off one leaves the Notifications page.
 */
export const useKeepContactDone = () => {
  const refresh = useRefreshFollowUps()
  return useMutation<KeepContactDoneResult, Error, string>({
    mutationFn: markDone,
    onSuccess: (_, id) => refresh(id),
  })
}

/**
 * « Reporter » (and « Annuler » after « C'est fait »): the reminder comes
 * back on that date. A repeating follow-up keeps its rhythm.
 */
export const usePostponeKeepContact = () => {
  const refresh = useRefreshFollowUps()
  return useMutation<{ date: string }, Error, { id: string; date: string }>({
    mutationFn: postpone,
    onSuccess: (_, { id }) => refresh(id),
  })
}
